import React, { createContext, useState, useContext } from 'react';
import { 
    Alert,
    Snackbar,
    Dialog,
    DialogContent,
    IconButton,
    Typography,
    Box,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';

const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

const PROMINENT_SEVERITIES = ['warning', 'error'];

const PROMINENT_COLORS = {
    warning: { bg: '#fff8e1', border: '#f59e0b', icon: '#d97706', text: '#78350f' },
    error:   { bg: '#fef2f2', border: '#ef4444', icon: '#dc2626', text: '#7f1d1d' },
};

const ProminentIcon = ({ severity }) => {
    const color = PROMINENT_COLORS[severity]?.icon;
    return severity === 'error'
        ? <ErrorOutlineIcon sx={{ fontSize: 40, color }} />
        : <WarningAmberIcon sx={{ fontSize: 40, color }} />;
};

export const ToastProvider = ({ children }) => {
    const [toast, setToast] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const showToast = (message, severity = 'success', options = {}) => {
        setToast({ open: true, message, severity, inline: options.inline ?? false });
    };

    const closeToast = () => {
        setToast({ ...toast, open: false });
    };

    const isProminent = PROMINENT_SEVERITIES.includes(toast.severity) && !toast.inline;
    const prominentColors = PROMINENT_COLORS[toast.severity];

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}

            {isProminent ? (
                <Dialog
                    open={toast.open}
                    onClose={closeToast}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            border: `2px solid ${prominentColors?.border}`,
                            backgroundColor: prominentColors?.bg,
                            boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
                            direction: 'rtl',
                        }
                    }}
                >
                    <DialogContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <ProminentIcon severity={toast.severity} />
                            <Typography
                                sx={{
                                    flex: 1,
                                    fontSize: '1.05rem',
                                    fontWeight: 600,
                                    color: prominentColors?.text,
                                    lineHeight: 1.6,
                                    mt: '6px',
                                }}
                            >
                                {toast.message}
                            </Typography>
                            <IconButton
                                onClick={closeToast}
                                size="small"
                                sx={{ color: prominentColors?.text, mt: '-4px', mr: '-8px' }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </DialogContent>
                </Dialog>
            ) : (
                <Snackbar
                    open={toast.open}
                    autoHideDuration={6000}
                    onClose={closeToast}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    sx={{ 
                        mb: 2,
                        mr: 2,
                        '& .MuiPaper-root': { direction: 'rtl' }
                    }}
                >
                    <Alert
                        severity={toast.severity}
                        onClose={closeToast}
                        sx={{
                            width: '100%',
                            '& .MuiAlert-icon': {
                                marginRight: '0',
                                marginLeft: '12px'
                            }
                        }}
                    >
                        {toast.message}
                    </Alert>
                </Snackbar>
            )}
        </ToastContext.Provider>
    );
};